/**
 * Microfone da sala.
 *
 * Duas camadas de supressao de ruido, e elas se somam:
 *
 * 1. **O navegador** — `echoCancellation`, `noiseSuppression` e
 *    `autoGainControl` sao os mesmos algoritmos que rodam no Meet. Matam ruido
 *    constante (ventilador, ar-condicionado) e o eco da caixa de som, de graca.
 * 2. **RNNoise** — rede neural em WebAssembly (152 KB), boa justamente no que a
 *    primeira nao pega: ruido que vai e vem, como teclado, cachorro e obra na
 *    rua. E o tipo de supressao que o Discord vende como Krisp.
 *
 * O modelo so e baixado quando a pessoa liga o microfone.
 */

/** Faixa que o RNNoise entende — o AudioContext e criado nela. */
const RNNOISE_SAMPLE_RATE = 48_000
export interface VoicePipeline {
    /** O que vai pro ar. */
    stream: MediaStream
    /** Encerra o processamento. NAO fecha o microfone — quem abriu e que fecha. */
    stop: () => Promise<void>
}

export function microphoneSupported(): boolean {
    return typeof navigator !== 'undefined'
        && !!navigator.mediaDevices
        && typeof navigator.mediaDevices.getUserMedia === 'function'
}

/**
 * Microfones disponiveis. Como nas cameras, os rotulos so aparecem depois da
 * primeira permissao concedida.
 */
export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
    if (!microphoneSupported() || typeof navigator.mediaDevices.enumerateDevices !== 'function') return []
    try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        return devices.filter((device) => device.kind === 'audioinput')
    } catch {
        return []
    }
}

/**
 * Abre o microfone com a supressao do navegador ligada.
 *
 * Sem `echoCancellation` quem estiver sem fone devolve pra sala o audio que
 * acabou de ouvir — e o resultado e aquele eco que inviabiliza a conversa.
 */
export async function captureMicrophone(deviceId?: string): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia({
        audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            ...(deviceId ? { deviceId: { exact: deviceId } } : {})
        },
        video: false
    })
}

/**
 * Passa o microfone pelo RNNoise.
 *
 * O import e dinamico porque o wasm so interessa a quem liga o microfone —
 * quem so assiste nao paga por ele.
 */
export async function createVoicePipeline(source: MediaStream): Promise<VoicePipeline> {
    const [{ RnnoiseWorkletNode, loadRnnoise }, workletUrl, wasmUrl, simdWasmUrl] = await Promise.all([
        import('@sapphi-red/web-noise-suppressor'),
        import('@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url').then((m) => m.default),
        import('@sapphi-red/web-noise-suppressor/rnnoise.wasm?url').then((m) => m.default),
        import('@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url').then((m) => m.default)
    ])

    // O RNNoise opera a 48 kHz: deixar o contexto na taxa do dispositivo faria
    // o worklet processar quadros do tamanho errado.
    const context = new AudioContext({ sampleRate: RNNOISE_SAMPLE_RATE })
    /**
     * Contexto suspenso nao processa nada — e o que sai do
     * `MediaStreamDestination` e silencio absoluto.
     *
     * Aqui isso seria o pior tipo de falha: a pessoa entra na voz, ve o proprio
     * botao de microfone ligado e fala sem que uma unica amostra chegue em
     * ninguem. A politica de autoplay so libera o contexto depois de um gesto,
     * e "entrar na voz" e um clique — mas navegador com a politica mais
     * apertada (ou aba restaurada) pode nascer suspenso mesmo assim.
     */
    await context.resume().catch(() => undefined)
    const binary = await loadRnnoise({ url: wasmUrl, simdUrl: simdWasmUrl })
    await context.audioWorklet.addModule(workletUrl)

    const input = context.createMediaStreamSource(source)
    const denoise = new RnnoiseWorkletNode(context, {
        wasmBinary: binary,
        // Voz e mono: processar dois canais dobraria o custo por nada.
        maxChannels: 1
    })
    const destination = context.createMediaStreamDestination()

    input.connect(denoise)
    denoise.connect(destination)

    return {
        stream: destination.stream,
        stop: async () => {
            try {
                input.disconnect()
                denoise.disconnect()
                denoise.port.close()
                // Fechar o contexto libera a thread de audio; sem isso ela fica
                // viva depois que a pessoa sai da voz.
                await context.close()
            } catch {
                // Ja fechado — nada a fazer.
            }
        }
    }
}

/**
 * Mede o quanto uma stream esta "falando", de 0 a 1.
 *
 * Serve pro anel de quem esta falando. Roda em cada cliente sobre o audio que
 * ele ja recebe: nenhuma mensagem extra circula pra isso.
 */
export function createLevelMeter(stream: MediaStream, context: AudioContext) {
    const source = context.createMediaStreamSource(stream)
    const analyser = context.createAnalyser()
    // Janela curta: o indicador precisa acompanhar a fala, nao a media dela.
    analyser.fftSize = 512
    analyser.smoothingTimeConstant = 0.4
    source.connect(analyser)

    const buffer = new Float32Array(analyser.fftSize)

    return {
        /** Volume atual (RMS). Fala normal fica por volta de 0,05 a 0,3. */
        read(): number {
            analyser.getFloatTimeDomainData(buffer)
            let sum = 0
            for (const sample of buffer) sum += sample * sample
            return Math.sqrt(sum / buffer.length)
        },
        stop() {
            try {
                source.disconnect()
                analyser.disconnect()
            } catch {
                // Contexto ja fechado.
            }
        }
    }
}
